import { apiJson } from "../utils/api.js";
import { handleApiError } from "../utils/errorHandler.js";

/**
 * Fetch all clients who have purchased from the chef (paginated over clients)
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Number of clients per page (default: 10)
 * @param {number} params.offset - Number of clients to skip (default: 0)
 * @param {string} params.search - Search by name, phone number or email (optional)
 * @returns {Promise<{ users: Array, count: number }>} API response shape
 */
export const getClients = async ({ limit = 10, offset = 0, search = "" } = {}) => {
    try {
        const params = new URLSearchParams();
        if (limit) params.append("limit", limit);
        if (offset) params.append("offset", offset);
        if (search) params.append("search", search);

        const response = await apiJson.get(`/kms/client?${params.toString()}`);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: Failed to fetch clients. Please try again later."
        });
        throw error;
    }
};

const TELEGRAM_CLIENTS_PAGE_SIZE = 200;

const unwrapTelegramClients = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.clients)) return data.clients;
    if (Array.isArray(data?.content)) return data.content;
    return [];
};

/**
 * Fetch Telegram-linked customers for the chef (KMS). Walks pages because the API defaults to 10.
 * Each row includes companyId / companyName when the customer belongs to a partner company.
 * @param {Object} options - Options
 * @param {number|string} [options.companyId] - Filter customers by partner company id (filtered server-side)
 * @returns {Promise<Array>} List of telegram customer DTOs
 */
export const getTelegramClients = async ({ companyId } = {}) => {
    try {
        const all = [];
        let offset = 0;
        let total = Infinity;
        while (offset < total) {
            const params = new URLSearchParams();
            params.append("limit", String(TELEGRAM_CLIENTS_PAGE_SIZE));
            params.append("offset", String(offset));
            if (companyId != null && companyId !== "") {
                params.append("company_id", String(companyId));
            }
            const response = await apiJson.get(`/kms/client/telegram?${params.toString()}`);
            const data = response.data;
            const page = unwrapTelegramClients(data);
            const count = Array.isArray(data) ? page.length : Number(data?.count);
            total = Number.isFinite(count) ? count : page.length;
            all.push(...page);
            if (!page.length) break;
            offset += page.length;
            if (offset > 10000) break;
        }
        return all;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: Failed to fetch Telegram clients."
        });
        throw error;
    }
};

/**
 * Update verification flag for a Telegram customer (customer id from /kms/client/telegram)
 * @param {number|string} customerId - Telegram customer id
 * @param {boolean} verified
 */
export const verifyTelegramCustomer = async (customerId, verified) => {
    try {
        const response = await apiJson.patch(`/kms/client/${customerId}/verification`, { verified });
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: Failed to update customer verification."
        });
        throw error;
    }
};
